import CreditTermsCache from "@/app/caching/caches/creditTermsCache";
import DeliveryAreasCache from "@/app/caching/caches/deliveryAreasCache";
import OutletRatingsCache from "@/app/caching/caches/outletRatingsCache";
import SalesAreasCache from "@/app/caching/caches/salesAreasCache";
import SalesCodeCache from "@/app/caching/caches/salesCodeCache";
import TaskTypesCache from "@/app/caching/caches/taskTypesCache";
import TaskQueuesCache from "@/app/caching/caches/taskQueuesCache";
import UnitsOfMeasuresCache from "@/app/caching/caches/unitsOfMeasureCache";
import PackageTypesCache from "@/app/caching/caches/packageTypesCache";
import OutletSummariesCache from "@/app/caching/caches/outletSummariesCache";
import type { EntityCacheConstructor } from "@/app/caching/apiCache";
import apiCache from "@/app/caching/apiCache";
import ItemsCache from "@/app/caching/caches/itemCache";
import GroupsCache from "@/app/caching/caches/groupsCache";
import OutletTypesCache from "@/app/caching/caches/outletTypesCache";
import StockLocationsCache from "@/app/caching/caches/stockLocationsCache";
import VatCodesCache from "@/app/caching/caches/vatCodesCache";
import CouriersCache from "@/app/caching/caches/couriersCache";
import OutletSubTypesCache from "@/app/caching/caches/outletSubTypesCache";
import PriceListsCache from "@/app/caching/caches/priceListsCache";
import LeadsCache from "@/app/caching/caches/leadsCache";
import PalletTypesCache from "@/app/caching/caches/palletTypesCache";
import VesselsCache from "@/app/caching/caches/vesselsCache";
import VesselTypesCache from "@/app/caching/caches/vesselTypesCache";
import VesselStatesCache from "@/app/caching/caches/vesselStatesCache";
import CollectionTimesCache from "@/app/caching/caches/collectionTimesCache";
import GlCodesCache from "@/app/caching/caches/glCodesCache";
import StockGroupsCache from "@/app/caching/caches/stockGroupsCache";
import PricingCategoriesCache from "@/app/caching/caches/pricingCategoriesCache";
import SupplierManufacturersCache from "@/app/caching/caches/supplierManufacturersCache";
import ProductBrandsCache from "@/app/caching/caches/productBrandsCache";
import RecipesCache from "@/app/caching/caches/recipesCache";
import CommunicationSubjectsCache from "./caches/communicationSubjectsCache";
import CommunicationTypesCache from "./caches/communicationTypesCache";
import RegionalTaxesCache from "@/app/caching/caches/regionalTaxesCache";

class BuildCache<T extends Record<string, EntityCacheConstructor>> {
  private _blueprint: T;

  constructor(blueprint: T) {
    this._blueprint = blueprint;
  }

  private extend<TMore extends Record<string, EntityCacheConstructor>>(
    additional: TMore
  ) {
    return new BuildCache<T & TMore>(
      Object.assign({}, this._blueprint, additional)
    );
  }

  build() {
    return apiCache.buildCacheSet(this._blueprint);
  }

  async buildAndUpdate() {
    const cache = this.build();
    await cache.update();
    return cache;
  }

  withCreditTerms() {
    return this.extend({ creditTerms: CreditTermsCache });
  }

  withCollectionTimes() {
    return this.extend({ collectionTimes: CollectionTimesCache });
  }

  withCommunicationSubjects() {
    return this.extend({ communicationSubjects: CommunicationSubjectsCache });
  }

  withCommunicationTypes() {
    return this.extend({ communicationTypes: CommunicationTypesCache });
  }

  withCouriers() {
    return this.extend({ couriers: CouriersCache });
  }

  withDeliveryAreas() {
    return this.extend({ deliveryAreas: DeliveryAreasCache });
  }

  withGlCodes() {
    return this.extend({ glCodes: GlCodesCache });
  }

  withGroups() {
    return this.extend({ groups: GroupsCache });
  }

  withItems() {
    return this.extend({ items: ItemsCache });
  }

  withLeads() {
    return this.extend({ leads: LeadsCache });
  }

  withOutletRatings() {
    return this.extend({ outletRatings: OutletRatingsCache });
  }

  withOutletSummaries() {
    return this.extend({ outlets: OutletSummariesCache });
  }

  withOutletSubTypes() {
    return this.extend({ outletSubTypes: OutletSubTypesCache });
  }

  withOutletTypes() {
    return this.extend({ outletTypes: OutletTypesCache });
  }

  withPackageTypes() {
    return this.extend({ packageTypes: PackageTypesCache });
  }

  withPalletTypes() {
    return this.extend({ palletTypes: PalletTypesCache });
  }

  withPriceLists() {
    return this.extend({ priceLists: PriceListsCache });
  }

  withPricingCategories() {
    return this.extend({ pricingCategories: PricingCategoriesCache });
  }

  withProductBrands() {
    return this.extend({ productBrands: ProductBrandsCache });
  }

  withRecipes() {
    return this.extend({ recipes: RecipesCache });
  }

  withRegionalTaxes() {
    return this.extend({ regionalTaxes: RegionalTaxesCache });
  }

  withSalesAreas() {
    return this.extend({ salesAreas: SalesAreasCache });
  }

  withSalesCodes() {
    return this.extend({ salesCodes: SalesCodeCache });
  }

  withStockGroups() {
    return this.extend({ stockGroups: StockGroupsCache });
  }

  withStockLocations() {
    return this.extend({ stockLocations: StockLocationsCache });
  }

  withSupplierManufacturers() {
    return this.extend({ supplierManufacturers: SupplierManufacturersCache });
  }

  withTaskQueues() {
    return this.extend({ taskQueues: TaskQueuesCache });
  }

  withTaskTypes() {
    return this.extend({ taskTypes: TaskTypesCache });
  }

  withUnitsOfMeasure() {
    return this.extend({ unitsOfMeasure: UnitsOfMeasuresCache });
  }

  withVatCodes() {
    return this.extend({ vatCodes: VatCodesCache });
  }

  withVessels() {
    return this.extend({ vessels: VesselsCache });
  }

  withVesselStates() {
    return this.extend({ vesselStates: VesselStatesCache });
  }

  withVesselTypes() {
    return this.extend({ vesselTypes: VesselTypesCache });
  }
}

export function buildCache() {
  return new BuildCache({});
}
